import { DroneMap } from './components/DroneMap'
import { ViolationsPanel } from './components/ViolationsPanel'
import { useDashboard } from './hooks/use-dashboard'
import { useDashboardStore } from './store'
import { formatTimestamp, redactFullName } from './utils'
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Map as MapIcon,
  Plane,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react'
import './App.css'

function App() {
  const { refreshDrones } = useDashboard()
  const {
    drones,
    violations,
    apiStatus,
    lastUpdate,
    violationStatus,
    selectedViolation,
    showViolationsPanel,
    setSelectedViolation,
    toggleViolationsPanel,
  } = useDashboardStore()

  const sortedViolations = [...violations].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  )

  const latestViolation = sortedViolations[0]
  const { violationInLastHour, dronesInViolation } = useDashboardStore()
  const violationCount = violations.length

  return (
    <div className="app-container">
      {/* Top Bar */}
      <header className="topbar">
        <div className="brand">
          <ShieldAlert className="brand-icon" size={24} />
          <span className="brand-name">AIRGUARDIAN</span>
          <span className="brand-tagline">NFZ Monitoring System</span>
        </div>

        <div className="status-bar">
          <div className={`status-indicator status-${apiStatus}`}>
            {apiStatus === 'online' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
            <span>{apiStatus === 'online' ? 'SYSTEM ONLINE' : 'SYSTEM OFFLINE'}</span>
          </div>

          <div className="last-update">
            <Clock size={14} />
            <span>LAST SYNC: {lastUpdate}</span>
          </div>

          <button className="icon-button" onClick={() => void refreshDrones()} title="Refresh data">
            <RefreshCw size={16} />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* Left: Map */}
        <section className="map-section">
          <div className="panel-header">
            <div className="panel-title">
              <MapIcon size={18} />
              <span>LIVE TRACKING</span>
            </div>
            <div className="panel-stats">
              <span className="stat">
                <Plane size={14} />
                {drones.length} DRONES
              </span>
              <span className="stat stat-warning">
                <AlertTriangle size={14} />
                {dronesInViolation} IN NFZ
              </span>
            </div>
          </div>

          <div className="map-wrapper">
            <DroneMap drones={drones} />
          </div>

          <div className="map-legend">
            <div className="legend-item">
              <span className="legend-marker safe"></span>
              <span>OUTSIDE NFZ</span>
            </div>
            <div className="legend-item">
              <span className="legend-marker violation"></span>
              <span>INSIDE NFZ (VIOLATION)</span>
            </div>
            <div className="legend-item nfz-info">
              <span>NFZ RADIUS: 1000 UNITS</span>
            </div>
          </div>
        </section>

        {/* Right: Violations Panel */}
        {showViolationsPanel && (
          <aside className="violations-section">
            <ViolationsPanel
              violations={sortedViolations}
              selectedViolation={selectedViolation}
              onSelectViolation={setSelectedViolation}
              status={violationStatus}
            />
          </aside>
        )}
      </main>

      {/* Bottom Status Bar */}
      <footer className="status-footer">
        <div className="footer-stats">
          <div className="footer-stat">
            <Activity size={14} />
            <span>TOTAL VIOLATIONS: {violationCount}</span>
          </div>
          <div className="footer-stat">
            <Clock size={14} />
            <span>LAST HOUR: {violationInLastHour}</span>
          </div>
          {latestViolation && (
            <div className="footer-stat">
              <span>LATEST: {redactFullName(latestViolation.owner_first_name, latestViolation.owner_last_name)} ·{' '}
              {formatTimestamp(latestViolation.timestamp)}</span>
            </div>
          )}
        </div>

        <button className="toggle-button" onClick={toggleViolationsPanel}>
          {showViolationsPanel ? 'HIDE VIOLATIONS' : 'SHOW VIOLATIONS'}
        </button>
      </footer>
    </div>
  )
}

export default App
